/**
 * Main application component
 * Sets up the application theme, routing, internationalization, and global components
 * @module App
 */
import React from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import lightTheme from './theme/lightTheme';
import { RootState, store } from './redux/store';
import GlobalLoading from './connected-components/loading';
import AppRouter from './route';
import GlobalToast from './connected-components/toast';
import { I18nextProvider } from 'react-i18next';
import i18n from './translation/i18n';
import GlobalModal from './connected-components/globalModal';
import Preferences from './connected-components/preferences';
import darkTheme from './theme/darkTheme';
import { Toaster } from 'sonner';
import { tr } from './translation';
import { showGlobalModal } from './connected-components/globalModal/actions';
import { endSession } from './screens/auth/login/actions';
import { setMessages } from './screens/content/chat/reducer';

/**
 * App component is the root component of the application
 * Manages theme state, routing, and global UI components
 * @returns {JSX.Element} The rendered App component
 */
const App = () => {
  /** Get the current theme preference from Redux state */
  const { isDarkMode } = useSelector((state: RootState) => state.settings);
  /** Select the appropriate theme based on user preference */
  const theme = isDarkMode ? darkTheme() : lightTheme();

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={theme}>
        <Toaster position={'top-right'} />
        <AppRouter />
        <GlobalToast />
        <GlobalLoading />
        <GlobalModal />
        <Preferences
          onLogoutPressed={() => {
            showGlobalModal({
              title: tr('afterLogin.logoutTitle'),
              message: tr('afterLogin.logoutMessage'),
              onConfirm: () => {
                endSession();
                store.dispatch(setMessages({ messages: [] }));
              },
              type: 'question',
            });
          }}
        />
      </ThemeProvider>
    </I18nextProvider>
  );
};

export default App;
